import { WbsItemSearchConditionV2VO } from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { ReferencedEntityWithIcon } from '../../../../domain/value-object/ReferencedEntity'
import { getErrorCode } from '../../../../infrastructure/api/error'
import { pruneRootUuids } from '../wbsItemSearch'

export type ExcludeResult = {
  // 検索に使う条件。削除済みと他プロジェクトのWBSをどちらも除外する
  cleaned: WbsItemSearchConditionV2VO
  // 保存し直す条件。削除済みのWBSだけを除外する
  persisted: WbsItemSearchConditionV2VO
  hasDeleted: boolean
  hasOtherProject: boolean
}

type ReferencedEntitySettledResult = PromiseSettledResult<
  ReferencedEntityWithIcon | undefined
>

export const toUnchangedResult = (
  condition: WbsItemSearchConditionV2VO
): ExcludeResult => ({
  cleaned: condition,
  persisted: condition,
  hasDeleted: false,
  hasOtherProject: false,
})

// 現在のプロジェクト内で個別確認した結果から、見つからなかったuuidを選ぶ。
// 確認に失敗したものがあると実在する工程をプロジェクト外と誤判定するため、
// 1件でも失敗していれば判定できないものとして undefined を返す。
export const selectUnconfirmedUuids = (
  uuids: string[],
  results: ReferencedEntitySettledResult[]
): string[] | undefined => {
  if (results.some(r => r.status === 'rejected')) return undefined
  const confirmedUuids = new Set(
    results.flatMap(r =>
      r.status === 'fulfilled' && r.value ? [r.value.uuid] : []
    )
  )
  return uuids.filter(uuid => !confirmedUuids.has(uuid))
}

// プロジェクトを問わずに引き直した結果から、見つからない（NOT_FOUND）uuidだけを削除済みとして選ぶ。
// バックエンドは NotFoundException を HTTP 400 + code: NOT_FOUND で返すため、status ではなく code で判定する。
// それ以外の失敗は判別できないため、保存条件から消さないよう削除済みには含めない。
export const selectNotFoundUuids = (
  uuids: string[],
  results: PromiseSettledResult<unknown>[]
): string[] =>
  uuids.filter((_, i) => {
    const result = results[i]
    return (
      result?.status === 'rejected' &&
      getErrorCode(result.reason) === 'NOT_FOUND'
    )
  })

export const buildExcludeResult = (
  condition: WbsItemSearchConditionV2VO,
  outsideUuids: string[],
  deletedUuids: string[]
): ExcludeResult => {
  const outside = new Set(outsideUuids)
  const deleted = new Set(deletedUuids)
  return {
    cleaned: {
      ...condition,
      rootUuids: pruneRootUuids(
        condition.rootUuids,
        uuid => !outside.has(uuid)
      ),
    },
    persisted: {
      ...condition,
      rootUuids: pruneRootUuids(
        condition.rootUuids,
        uuid => !deleted.has(uuid)
      ),
    },
    hasDeleted: deletedUuids.length > 0,
    hasOtherProject: outsideUuids.length > deletedUuids.length,
  }
}

// スナックバーは最新の1件しか表示しないため、両方を除外したときは1つの文言にまとめる
export const selectExcludedMessageId = ({
  hasDeleted,
  hasOtherProject,
}: Pick<ExcludeResult, 'hasDeleted' | 'hasOtherProject'>):
  | string
  | undefined => {
  if (hasDeleted && hasOtherProject) {
    return 'wbs.search.rootUuids.deletedOrOtherProject.title'
  }
  if (hasDeleted) return 'wbs.search.rootUuids.excluded.title'
  if (hasOtherProject) return 'wbs.search.rootUuids.otherProject.title'
  return undefined
}
